import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import APIGateway from '@/api';
import { ContractStatus, ContractTaskStatus } from '@/types/dataContracts';

export const dataContractsKeys = {
  all: [ 'dataContracts' ] as const,
  list: [ 'dataContracts', 'list' ] as const,
  detail: ( contractId: string ) => [ ...dataContractsKeys.all, 'detail', contractId ] as const,
  contractStatus: ( contractId: string ) => [ ...dataContractsKeys.all, 'contractStatus', contractId ] as const,
  taskStatus: ( taskId: string ) => [ ...dataContractsKeys.all, 'taskStatus', taskId ] as const,
  contractCountBySecret: ( secretName: string ) => [ ...dataContractsKeys.all, 'contractCountBySecret', secretName ] as const
};

export function useContracts() {
  return useQuery( {
    queryKey: dataContractsKeys.list,
    queryFn: APIGateway.dataContracts.getContracts
  } );
}

export function useContract( contractId: string | null ) {
  return useQuery( {
    queryKey: dataContractsKeys.detail( contractId! ),
    queryFn: () => APIGateway.dataContracts.getContract( contractId! ),
    enabled: !!contractId,
    refetchInterval: ( query ) => {
      const status = query.state.data?.status;
      if ( status === ContractStatus.QUEUED || status === ContractStatus.PROCESSING || status === ContractStatus.DELETING ) {
        return 5000;
      }
      return false;
    }
  } );
}

export function useContractStatus( contractId: string | null ) {
  return useQuery( {
    queryKey: dataContractsKeys.contractStatus( contractId! ),
    queryFn: () => APIGateway.dataContracts.getContractStatus( contractId! ),
    enabled: !!contractId,
    refetchInterval: ( query ) => {
      const status = query.state.data?.contract_status;
      if ( status === ContractStatus.QUEUED || status === ContractStatus.PROCESSING || status === ContractStatus.DELETING ) {
        return 5000;
      }
      return false;
    }
  } );
}

export function useDeleteContract() {
  const queryClient = useQueryClient();
  return useMutation( {
    mutationFn: ( contractId: string ) => APIGateway.dataContracts.deleteContract( contractId ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.list } );
    }
  } );
}

export function useUpdateContract() {
  const queryClient = useQueryClient();
  return useMutation( {
    mutationFn: ( { contractId, file }: { contractId: string; file: File } ) => APIGateway.dataContracts.updateContract( contractId, file ),
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.list } );
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.detail( variables.contractId ) } );
    }
  } );
}

export function useValidateContract() {
  return useMutation( {
    mutationFn: ( file: File ) => APIGateway.dataContracts.validateContract( file )
  } );
}

export function useCreateContract() {
  const queryClient = useQueryClient();
  return useMutation( {
    mutationFn: ( file: File ) => APIGateway.dataContracts.createContract( file ),
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.list } );
    }
  } );
}

export function useContractCountBySecret( secretName: string | undefined ) {
  return useQuery( {
    queryKey: dataContractsKeys.contractCountBySecret( secretName! ),
    queryFn: () => APIGateway.dataContracts.getContractCountBySecret( secretName! ),
    enabled: !!secretName
  } );
}

export function useTaskStatus( taskId: string | null, enabled: boolean ) {
  return useQuery( {
    queryKey: dataContractsKeys.taskStatus( taskId! ),
    queryFn: () => APIGateway.dataContracts.getTaskStatus( taskId! ),
    enabled: enabled && !!taskId,
    refetchInterval: ( query ) => {
      const status = query.state.data?.status;
      if ( status === ContractTaskStatus.SUCCESS || status === ContractTaskStatus.FAILURE || status === ContractTaskStatus.REVOKED ) {
        return false;
      }
      return 2000;
    }
  } );
}
